(function () {
  'use strict';

  const SELECTORS = {
    section: 'section[data-section-id][data-collection-url]',
    toolbar: '.collection-toolbar',
    activeFilters: '.collection-active-filters',
    filters: '.collection-filters',
    products: '.collection-products',
    grid: '.product-grid',
    loadMore: '[data-load-more]',
    loadMoreStatus: '[data-load-more-status]',
    loadingOverlay: '[data-loading-overlay]',
    form: '#FacetFiltersForm',
    sort: '#SortBy',
    sortToggle: '[data-sort-toggle]',
    sortClose: '[data-sort-close], [data-sort-overlay]',
    sortSheet: '#CollectionSort',
    sortOption: '[data-sort-option]',
    sortCurrent: '[data-sort-current]',
    toggle: '[data-facets-toggle]',
    close: '[data-facets-close], [data-facets-overlay]',
    drawer: '#CollectionFilters',
    paginationLink: '.pagination a',
    activeChip: '.collection-active-filters__chip',
    filterValue: '.facets__value input[type="checkbox"]'
  };

  const SECTION_ATTR = {
    sectionId: 'data-section-id',
    collectionUrl: 'data-collection-url',
    paginationType: 'data-pagination-type',
    busy: 'aria-busy'
  };

  function getSection(element) {
    return element.closest(SELECTORS.section);
  }

  function setLoading(section, isLoading) {
    section.setAttribute(SECTION_ATTR.busy, isLoading ? 'true' : 'false');
    section.classList.toggle('is-loading', isLoading);
    const overlay = section.querySelector(SELECTORS.loadingOverlay);
    if (overlay) overlay.hidden = !isLoading;
  }

  function isDrawerMode(section) {
    return window.matchMedia('(max-width: 989px)').matches ||
      section.classList.contains('collection-grid-section--drawer');
  }

  function setDrawerVisibility(section) {
    const drawer = section.querySelector(SELECTORS.drawer);
    if (!drawer) return;
    if (isDrawerMode(section)) {
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
    } else {
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
    }
  }

  function openDrawer(section) {
    if (!isDrawerMode(section)) {
      const drawer = section.querySelector(SELECTORS.drawer);
      if (drawer) drawer.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const drawer = section.querySelector(SELECTORS.drawer);
    const toggles = section.querySelectorAll(SELECTORS.toggle);
    if (!drawer) return;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const panel = drawer.querySelector('.collection-filters__panel');
    if (panel) {
      const firstFocusable = panel.querySelector('button, input, select, a[href]');
      if (firstFocusable) firstFocusable.focus();
    }
    toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
  }

  function closeDrawer(section) {
    const drawer = section.querySelector(SELECTORS.drawer);
    const toggles = section.querySelectorAll(SELECTORS.toggle);
    if (!drawer) return;
    if (isDrawerMode(section)) {
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    } else {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = '';
    }
  }

  function openSortSheet(section) {
    if (!isDrawerMode(section)) return;
    const sheet = section.querySelector(SELECTORS.sortSheet);
    const toggle = section.querySelector(SELECTORS.sortToggle);
    if (!sheet) return;
    sheet.hidden = false;
    sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  function closeSortSheet(section) {
    const sheet = section.querySelector(SELECTORS.sortSheet);
    const toggle = section.querySelector(SELECTORS.sortToggle);
    if (!sheet) return;
    sheet.hidden = true;
    sheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function updateSortToggle(section) {
    const select = section.querySelector(SELECTORS.sort);
    const current = section.querySelector(SELECTORS.sortCurrent);
    if (!select || !current) return;
    const selected = select.options[select.selectedIndex];
    if (selected) current.textContent = selected.text;

    const options = section.querySelectorAll(SELECTORS.sortOption);
    options.forEach(function (option) {
      const isSelected = option.dataset.value === select.value;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  function handleSortOptionClick(e, section) {
    const option = e.target.closest(SELECTORS.sortOption);
    if (!option) return;
    const select = section.querySelector(SELECTORS.sort);
    if (!select) return;
    select.value = option.dataset.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    closeSortSheet(section);
  }

  function buildSectionUrl(base, sectionId, params) {
    const url = new URL(base, window.location.href);
    if (params) {
      const incoming = new URLSearchParams(params);
      incoming.forEach(function (value, key) {
        url.searchParams.set(key, value);
      });
    }
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  function cleanUrlForHistory(sectionUrl) {
    const url = new URL(sectionUrl);
    url.searchParams.delete('section_id');
    return url.toString();
  }

  function parseResponseHtml(html, sectionId) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector(`section[${SECTION_ATTR.sectionId}="${sectionId}"]`);
  }

  function updateLoadMoreStatus(section, newProducts) {
    const products = section.querySelector(SELECTORS.products);
    if (!products) return;
    const oldStatus = products.querySelector(SELECTORS.loadMoreStatus);
    const newStatus = newProducts ? newProducts.querySelector(SELECTORS.loadMoreStatus) : null;
    if (oldStatus && newStatus) {
      oldStatus.replaceWith(newStatus);
    } else if (oldStatus && !newStatus) {
      oldStatus.remove();
    } else if (!oldStatus && newStatus) {
      products.appendChild(newStatus);
    }
  }

  function updateLoadMoreButton(section, newProducts) {
    const products = section.querySelector(SELECTORS.products);
    if (!products) return;
    const oldButton = products.querySelector(SELECTORS.loadMore);
    const newButton = newProducts ? newProducts.querySelector(SELECTORS.loadMore) : null;

    if (oldButton && newButton) {
      oldButton.replaceWith(newButton);
    } else if (oldButton && !newButton) {
      oldButton.remove();
    } else if (!oldButton && newButton) {
      const pagination = products.querySelector(SELECTORS.paginationLink);
      if (pagination) {
        pagination.closest('.pagination').after(newButton);
      } else {
        products.appendChild(newButton);
      }
    }
  }

  function updateSection(section, newSection, mode) {
    const products = section.querySelector(SELECTORS.products);
    const newProducts = newSection.querySelector(SELECTORS.products);

    if (mode === 'append' && products && newProducts) {
      const grid = products.querySelector(SELECTORS.grid);
      const newGrid = newProducts.querySelector(SELECTORS.grid);
      if (grid && newGrid) {
        grid.insertAdjacentHTML('beforeend', newGrid.innerHTML);
      }
      updateLoadMoreButton(section, newProducts);
      updateLoadMoreStatus(section, newProducts);
    } else {
      const replaceMap = [
        [SELECTORS.toolbar, SELECTORS.toolbar],
        [SELECTORS.activeFilters, SELECTORS.activeFilters],
        [SELECTORS.filters, SELECTORS.filters],
        [SELECTORS.products, SELECTORS.products]
      ];
      replaceMap.forEach(function (pair) {
        const current = section.querySelector(pair[0]);
        const newEl = newSection.querySelector(pair[1]);
        if (current && newEl) current.replaceWith(newEl);
      });
    }

    closeDrawer(section);
    closeSortSheet(section);
    setDrawerVisibility(section);
    initPriceSliders(section);
    updateSortToggle(section);
    setLoading(section, false);

    if (section.dataset.paginationType === 'infinite') {
      initInfinite(section);
    }
  }

  async function fetchSection(url) {
    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(response.statusText);
    return response.text();
  }

  async function requestSection(section, requestUrl, mode, historyUrl) {
    if (section.dataset.isFetching === 'true') return;
    section.dataset.isFetching = 'true';
    if (mode !== 'append') {
      setLoading(section, true);
    } else {
      const loadMore = section.querySelector(SELECTORS.loadMore);
      if (loadMore) {
        loadMore.setAttribute('aria-disabled', 'true');
        loadMore.textContent = window.themeStrings.loading;
      }
    }

    try {
      const html = await fetchSection(requestUrl);
      const newSection = parseResponseHtml(html, section.dataset.sectionId);
      if (newSection) updateSection(section, newSection, mode);
      if (historyUrl) {
        window.history.pushState({ sectionId: section.dataset.sectionId }, '', cleanUrlForHistory(historyUrl));
      }
    } catch (err) {
      if (mode === 'append') {
        console.error('Collection load more error:', err);
        const loadMore = section.querySelector(SELECTORS.loadMore);
        if (loadMore) {
          loadMore.removeAttribute('aria-disabled');
          const label = window.themeStrings.loadMore;
          loadMore.textContent = label;
        }
      } else {
        console.error('Collection AJAX error:', err);
        window.location.assign(requestUrl);
      }
    } finally {
      section.dataset.isFetching = 'false';
    }
  }

  function handleFormSubmit(e, section) {
    e.preventDefault();
    const form = e.target;
    const url = buildSectionUrl(form.action, section.dataset.sectionId, new FormData(form));
    requestSection(section, url.toString(), 'replace', url.toString());
  }

  function handleSortChange(e, section) {
    const form = section.querySelector(SELECTORS.form);
    if (!form) return;
    const formData = new FormData(form);
    formData.set('sort_by', e.target.value);
    const url = buildSectionUrl(form.action, section.dataset.sectionId, formData);
    requestSection(section, url.toString(), 'replace', url.toString());
  }

  function handleLinkClick(e, section, link, mode) {
    e.preventDefault();
    const url = buildSectionUrl(link.href, section.dataset.sectionId);
    requestSection(section, url.toString(), mode, url.toString());
  }

  function handleLoadMore(e, section, mode) {
    const button = e.target.closest(SELECTORS.loadMore);
    if (!button) return;
    e.preventDefault();
    const url = buildSectionUrl(button.dataset.url, section.dataset.sectionId);
    requestSection(section, url.toString(), mode, null);
  }

  function initInfinite(section) {
    if (section.dataset.paginationType !== 'infinite') return;
    const button = section.querySelector(SELECTORS.loadMore);
    if (!button) return;
    if (button.dataset.infiniteObserver === 'true') return;
    button.dataset.infiniteObserver = 'true';

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && section.dataset.isFetching !== 'true') {
          handleLoadMore({ target: button, preventDefault: function () {} }, section, 'append');
        }
      });
    }, { rootMargin: '200px' });

    observer.observe(button);
  }

  function formatMoney(value, currency, lang) {
    try {
      return new Intl.NumberFormat(lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value);
    } catch (err) {
      return value.toString();
    }
  }

  function initPriceSliders(section) {
    const sliders = section.querySelectorAll('[data-price-slider]');
    sliders.forEach(function (slider) {
      const track = slider.querySelector('[data-price-slider-track]');
      const fill = slider.querySelector('[data-price-slider-fill]');
      const minHandle = slider.querySelector('[data-price-slider-handle="min"]');
      const maxHandle = slider.querySelector('[data-price-slider-handle="max"]');
      const minDisplay = slider.querySelector('[data-price-slider-min]');
      const maxDisplay = slider.querySelector('[data-price-slider-max]');
      const resetBtn = slider.querySelector('[data-price-slider-reset]');
      const minInput = document.getElementById('Filter-Price-GTE');
      const maxInput = document.getElementById('Filter-Price-LTE');

      if (!track || !fill || !minHandle || !maxHandle || !minInput || !maxInput) return;

      const currencyCode = slider.dataset.currencyCode || Shopify.currency.active || 'USD';
      const lang = document.documentElement.lang || 'en';

      const min = parseFloat(minInput.min) || 0;
      const max = parseFloat(maxInput.max) || 0;
      if (max <= min) return;

      let minValue = minInput.value ? parseFloat(minInput.value) : min;
      let maxValue = maxInput.value ? parseFloat(maxInput.value) : max;
      if (isNaN(minValue) || minValue < min) minValue = min;
      if (isNaN(maxValue) || maxValue > max) maxValue = max;
      if (minValue > maxValue) minValue = maxValue;

      function setInputValues() {
        if (minValue === min && maxValue === max) {
          minInput.value = '';
          maxInput.value = '';
        } else {
          minInput.value = minValue.toFixed(2);
          maxInput.value = maxValue.toFixed(2);
        }
      }

      function updateDisplay() {
        const minPercent = ((minValue - min) / (max - min)) * 100;
        const maxPercent = ((maxValue - min) / (max - min)) * 100;
        minHandle.style.left = minPercent + '%';
        maxHandle.style.left = maxPercent + '%';
        fill.style.left = minPercent + '%';
        fill.style.width = (maxPercent - minPercent) + '%';
        if (minDisplay) minDisplay.textContent = formatMoney(minValue, currencyCode, lang);
        if (maxDisplay) maxDisplay.textContent = formatMoney(maxValue, currencyCode, lang);
      }

      function valueFromPointer(clientX) {
        const rect = track.getBoundingClientRect();
        let percent = (clientX - rect.left) / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        return min + percent * (max - min);
      }

      let activeHandle = null;

      function onPointerMove(e) {
        if (!activeHandle) return;
        let value = valueFromPointer(e.clientX);
        if (activeHandle === 'min') {
          minValue = Math.min(value, maxValue);
        } else {
          maxValue = Math.max(value, minValue);
        }
        setInputValues();
        updateDisplay();
      }

      function onPointerUp() {
        activeHandle = null;
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
      }

      function onPointerDown(e, handle) {
        e.preventDefault();
        activeHandle = handle;
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
      }

      minHandle.addEventListener('pointerdown', function (e) { onPointerDown(e, 'min'); });
      maxHandle.addEventListener('pointerdown', function (e) { onPointerDown(e, 'max'); });

      if (resetBtn) {
        resetBtn.addEventListener('click', function () {
          minValue = min;
          maxValue = max;
          setInputValues();
          updateDisplay();
        });
      }

      setInputValues();
      updateDisplay();
    });
  }

  function onDocumentClick(e) {
    const toggle = e.target.closest(SELECTORS.toggle);
    if (toggle) {
      const section = getSection(toggle);
      if (section) openDrawer(section);
      return;
    }

    const sortToggle = e.target.closest(SELECTORS.sortToggle);
    if (sortToggle) {
      const section = getSection(sortToggle);
      if (section) openSortSheet(section);
      return;
    }

    const sortOption = e.target.closest(SELECTORS.sortOption);
    if (sortOption) {
      const section = getSection(sortOption);
      if (section) handleSortOptionClick(e, section);
      return;
    }

    const sortClose = e.target.closest(SELECTORS.sortClose);
    if (sortClose) {
      const section = getSection(sortClose);
      if (section) closeSortSheet(section);
      return;
    }

    const close = e.target.closest(SELECTORS.close);
    if (close) {
      const section = getSection(close);
      if (section) closeDrawer(section);
      return;
    }

    const activeChip = e.target.closest(SELECTORS.activeChip);
    if (activeChip) {
      const section = getSection(activeChip);
      if (section) handleLinkClick(e, section, activeChip, 'replace');
      return;
    }

    const pageLink = e.target.closest(SELECTORS.paginationLink);
    if (pageLink) {
      const section = getSection(pageLink);
      if (section) handleLinkClick(e, section, pageLink, 'replace');
      return;
    }

    const loadMore = e.target.closest(SELECTORS.loadMore);
    if (loadMore && !loadMore.dataset.infinite) {
      const section = getSection(loadMore);
      if (section) handleLoadMore(e, section, 'append');
      return;
    }
  }

  function onDocumentChange(e) {
    if (e.target.matches(SELECTORS.sort)) {
      const section = getSection(e.target);
      if (section) {
        handleSortChange(e, section);
        updateSortToggle(section);
        closeSortSheet(section);
      }
      return;
    }

    const filterValue = e.target.closest(SELECTORS.filterValue);
    if (filterValue) {
      // Optionally auto-apply on desktop filter values. Current design waits for Apply.
      // Uncomment below to enable auto-apply for checkbox changes.
      // const section = getSection(filterValue);
      // if (section) {
      //   const form = section.querySelector(SELECTORS.form);
      //   if (form) {
      //     const url = buildSectionUrl(form.action, section.dataset.sectionId, new FormData(form));
      //     requestSection(section, url.toString(), 'replace', url.toString());
      //   }
      // }
    }
  }

  function onDocumentSubmit(e) {
    if (e.target.matches(SELECTORS.form)) {
      const section = getSection(e.target);
      if (section) handleFormSubmit(e, section);
    }
  }

  function onDocumentKeydown(e) {
    if (e.key === 'Escape') {
      document.querySelectorAll(SELECTORS.section).forEach(function (section) {
        const isMobile = window.matchMedia('(max-width: 989px)').matches;
        if (isMobile || section.classList.contains('collection-grid-section--drawer')) {
          closeDrawer(section);
        }
        if (isMobile) closeSortSheet(section);
      });
    }
  }

  function onPopState(e) {
    if (e.state && e.state.sectionId) {
      const section = document.querySelector(`section[${SECTION_ATTR.sectionId}="${e.state.sectionId}"]`);
      if (section) {
        const url = buildSectionUrl(window.location.href, section.dataset.sectionId);
        requestSection(section, url.toString(), 'replace', null);
      }
    }
  }

  let resizeTimer;
  function debounceResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll(SELECTORS.section).forEach(function (section) {
        if (!isDrawerMode(section)) {
          closeDrawer(section);
          setDrawerVisibility(section);
        } else {
          setDrawerVisibility(section);
        }
      });
    }, 150);
  }

  function init() {
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('change', onDocumentChange);
    document.addEventListener('submit', onDocumentSubmit);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('popstate', onPopState);

    document.querySelectorAll(SELECTORS.section).forEach(function (section) {
      setDrawerVisibility(section);
      initPriceSliders(section);
      updateSortToggle(section);
      if (section.dataset.paginationType === 'infinite') {
        initInfinite(section);
      }
    });

    window.addEventListener('resize', debounceResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
